// GtSourcing — the sourcing brief and the Suggested tray, shared by benches and
// roles.
//
// The brief says who to look for. "Search your pool" looks through everyone we
// hold, "Search your benches" (roles only) through people already on a bench,
// and "Search the web" finds people we do not hold yet. People from the web are
// not saved as candidates unless someone accepts them. Each
// person comes back with a fit score, the must-haves we can and cannot see,
// reasons and the evidence behind them. Nothing goes onto the bench or role
// until someone accepts it; dismissing asks why, and both are kept. Nobody is
// contacted.
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import API_BASE from '../config';
import { GtSelect } from './GtControls';
import GtNoticeModal from './GtNoticeModal';
import GtBulkNoticeModal from './GtBulkNoticeModal';

const BAND_LABEL = { green: 'Strong fit', amber: 'Possible fit', slate: 'Weak fit' };
const ACCEPT_CHUNK = 6;
const DISMISS = [
  { value: 'wrong_skills', label: 'Wrong skills' },
  { value: 'not_senior_enough', label: 'Not senior enough' },
  { value: 'too_senior', label: 'Too senior' },
  { value: 'wrong_location', label: 'Wrong location' },
  { value: 'not_available', label: 'Not available or not looking' },
  { value: 'already_known', label: 'Already known to us' },
  { value: 'other', label: 'Other' },
];
const DISMISS_LABEL = {};
DISMISS.forEach(d => { DISMISS_LABEL[d.value] = d.label; });
const VIEWS = [
  { key: 'new', label: 'Suggested' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'dismissed', label: 'Dismissed' },
];

function money(n) { return '£' + Number(n || 0).toFixed(2); }
function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
function niceDate(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function words(v) { return v ? String(v).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : ''; }
function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }
function lines(text) { return String(text || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean); }

// A signed-in fetch: JSON in and out, throws with the server's own message.
export function useSourcingFetch(getToken) {
  return useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const headers = { ...(opts.headers || {}), Authorization: 'Bearer ' + token };
    if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    const res = await fetch(API_BASE + path, { ...opts, headers });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(body.message || body.detail || body.error || ('request failed: ' + res.status));
      err.status = res.status; err.body = body;
      throw err;
    }
    return body;
  }, [getToken]);
}

// ── The brief ──────────────────────────────────────────────────────────────
function BriefModal({ kind, brief, authFetch, base, flash, onClose, onSaved }) {
  const [f, setF] = useState({
    must: (brief.must_haves || []).join('\n'),
    nice: (brief.nice_to_haves || []).join('\n'),
    seniority: brief.seniority || '',
    locations: (brief.locations || []).join('\n'),
    exclusions: brief.exclusions || '',
    notes: brief.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  function set(k, v) { setF(p => ({ ...p, [k]: v })); }
  async function save() {
    setBusy(true); setError('');
    try {
      const r = await authFetch(base + '/brief', {
        method: 'PUT',
        body: JSON.stringify({
          must_haves: lines(f.must), nice_to_haves: lines(f.nice), seniority: f.seniority,
          locations: lines(f.locations), exclusions: f.exclusions, notes: f.notes,
          expertise_domain: brief.expertise_domain || null,
        }),
      });
      flash('Brief saved');
      onSaved(r.brief);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsr-briefmodal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Sourcing brief</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          <p className="gtbn-help">Describe the work, never the person: no age, gender, nationality or similar. One item per line.</p>
          <label className="gtc-field"><span>Must-haves</span>
            <textarea rows={4} value={f.must} placeholder={'e.g. Python in production\nBuilt LLM evaluation pipelines'} onChange={e => set('must', e.target.value)} />
          </label>
          <label className="gtc-field"><span>Nice-to-haves</span>
            <textarea rows={3} value={f.nice} placeholder={'e.g. PyTorch\nPublished research'} onChange={e => set('nice', e.target.value)} />
          </label>
          <div className="gtsr-two">
            <label className="gtc-field"><span>Seniority</span>
              <input value={f.seniority} maxLength={60} placeholder="e.g. senior" onChange={e => set('seniority', e.target.value)} />
            </label>
            <label className="gtc-field"><span>Locations <em className="gtbn-opt">matched by us, not the AI</em></span>
              <textarea rows={2} value={f.locations} placeholder={'e.g. London\nRemote UK'} onChange={e => set('locations', e.target.value)} />
              <em className="gtsr-fieldnote">Left blank, it takes the {kind === 'role' ? 'role' : 'bench'}'s location. Type Anywhere to search worldwide. People in another country become weaker matches.</em>
            </label>
          </div>
          <label className="gtc-field"><span>Exclusions</span>
            <input value={f.exclusions} maxLength={1000} placeholder="e.g. Nobody currently at the client" onChange={e => set('exclusions', e.target.value)} />
          </label>
          <label className="gtc-field"><span>Anything else</span>
            <textarea rows={3} value={f.notes} placeholder="What good looks like, in plain words." onChange={e => set('notes', e.target.value)} />
          </label>
          {kind === 'role' && <p className="gtbn-help">This started from the role's extracted profile. Changing it here does not change the profile.</p>}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save brief'}</button>
        </div>
      </div>
    </div>
  );
}

function BriefCard({ brief, saved, kind, plan, onEdit, onEditPlan, onBuildPlan, onSaveBoolean, building }) {
  const must = brief.must_haves || [];
  const nice = brief.nice_to_haves || [];
  const empty = !must.length && !nice.length && !brief.notes;
  const kindOf = i => {
    const m = plan && plan.current && (plan.must || []).find(x => x.i === i + 1);
    return m ? m.kind : null;
  };
  return (
    <section className="gtsr-brief">
      <div className="gtsr-brief-h">
        <div>
          <h3>Sourcing brief</h3>
          <span className="gtsr-sub">{saved ? 'Who we look for.' : kind === 'role' ? 'Taken from the role profile. Check it, then search.' : 'Taken from the bench. Add must-haves, then search.'}</span>
        </div>
        <button type="button" className="gtc-btn-ghost" onClick={onEdit}>{empty ? 'Write the brief' : 'Edit brief'}</button>
      </div>
      {empty ? (
        <p className="gtsr-muted">No brief yet. Add the must-haves and nice-to-haves, and we will look through your pool for people who fit.</p>
      ) : (
        <div className="gtsr-brief-grid">
          {must.length > 0 && (
            <div className="gtsr-brief-col">
              <span className="gtsr-k">Must-haves</span>
              <ul className="gtsr-reqlist">{must.map((m, i) => (
                <li key={i} className={'gtsr-req ' + (kindOf(i) === 'trait' ? 'gtsr-req--trait' : 'gtsr-req--must')}>
                  {m}{kindOf(i) === 'trait' && <span className="gtsr-traitbadge" title="A personal quality profiles rarely show. Not searched for, and never counts against anyone.">trait</span>}
                </li>
              ))}</ul>
            </div>
          )}
          {nice.length > 0 && (
            <div className="gtsr-brief-col">
              <span className="gtsr-k">Nice-to-haves</span>
              <div className="gtsr-chips">{nice.map((m, i) => <span key={i} className="gtsr-chip">{m}</span>)}</div>
            </div>
          )}
          {(brief.seniority || (brief.locations || []).length > 0 || brief.expertise_domain || brief.exclusions) && (
            <div className="gtsr-brief-col gtsr-brief-facts">
              {brief.expertise_domain && <div><span className="gtsr-k">Field</span><b>{words(brief.expertise_domain)}</b></div>}
              {brief.seniority && <div><span className="gtsr-k">Seniority</span><b>{brief.seniority}</b></div>}
              {(brief.locations || []).length > 0 && <div><span className="gtsr-k">Locations</span><b>{brief.locations.join(', ')}</b></div>}
              {brief.exclusions && <div><span className="gtsr-k">Exclusions</span><b>{brief.exclusions}</b></div>}
            </div>
          )}
        </div>
      )}
      {!empty && !(brief.locations || []).length && (
        <div className="gtsr-noplace">No location set, so searches look worldwide. Set the {kind === 'role' ? 'role' : 'bench'}'s location, or add one with Edit brief.</div>
      )}
      {!empty && <PlanBlock plan={plan} onEdit={onEditPlan} onBuild={onBuildPlan} onSaveBoolean={onSaveBoolean} building={building} />}
    </section>
  );
}

// The search plan: how we look for people who do this work, whatever their title.
// A quick check while typing. The server reads it properly on save.
function booleanProblem(t) {
  const s = String(t || '');
  if ((s.match(/"/g) || []).length % 2) return 'A quote is not closed.';
  let depth = 0;
  for (const ch of s.replace(/"[^"]*"/g, '')) {
    if (ch === '(') depth += 1;
    if (ch === ')') { depth -= 1; if (depth < 0) return 'A closing bracket has no opening one.'; }
  }
  if (depth > 0) return 'A bracket is not closed.';
  if (/\b(AND|OR|NOT)\s*$/i.test(s.trim())) return 'It ends with ' + s.trim().split(/\s+/).pop().toUpperCase() + '.';
  return '';
}

function PlanBlock({ plan, onEdit, onBuild, onSaveBoolean, building }) {
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [boolErr, setBoolErr] = useState('');
  if (!plan || !plan.current) {
    return (
      <div className="gtsr-plan gtsr-plan--empty">
        <div>
          <b>Search plan</b>
          <span>{plan ? 'The brief has changed since the plan was written. Searching will write a fresh one, or build it now to check it first.'
            : 'Built by AI from the brief, the way a sourcer works: the job titles people doing this work hold, the skills that show it, and a Boolean string. Searching builds it, or build it now to check it first.'}</span>
        </div>
        <button type="button" className="gtc-btn-ghost" disabled={building} onClick={onBuild}>{building ? 'Building…' : plan ? 'Rebuild plan' : 'Build search plan'}</button>
      </div>
    );
  }
  async function copy() {
    try { await navigator.clipboard.writeText(plan.boolean || ''); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* not allowed */ }
  }
  return (
    <div className="gtsr-plan">
      <div className="gtsr-plan-h">
        <div>
          <b>Search plan</b>
          <span>{plan.edited ? 'Edited by you.' : plan.fallback ? 'Plain words from the brief: the AI could not be reached. Rebuild it to try again.' : 'Written by AI from the brief. Titles vary between companies, so we search for the work.'}</span>
        </div>
        <span className="gtsr-plan-acts">
          <button type="button" className="gtc-btn-ghost" disabled={building} onClick={onBuild}>{building ? 'Building…' : 'Rebuild'}</button>
          <button type="button" className="gtc-btn-ghost" onClick={onEdit}>Edit plan</button>
        </span>
      </div>
      {plan.work && <p className="gtsr-plan-work">{plan.work}</p>}
      <div className="gtsr-plan-grid">
        {(plan.titles || []).length > 0 && (
          <div><span className="gtsr-k">Job titles we search ({plan.titles.length})</span>
            <div className="gtsr-chips">{plan.titles.map((t, i) => <span key={i} className="gtsr-chip gtsr-chip--title">{t}</span>)}</div>
            {(plan.exclude_titles || []).length > 0 && <p className="gtsr-plan-not">Not: {plan.exclude_titles.join(', ')}</p>}
          </div>
        )}
        {(plan.skills || []).length > 0 && (
          <div><span className="gtsr-k">Skills and signals ({plan.skills.length})</span>
            <div className="gtsr-chips">{plan.skills.map((t, i) => <span key={i} className="gtsr-chip">{t}</span>)}</div>
          </div>
        )}
      </div>
      {draft !== null ? (
        <div className="gtsr-boolean gtsr-boolean--edit">
          <span className="gtsr-k">Boolean <em>AND, OR, NOT, "phrases", (brackets), and a * for any ending: sourc*</em></span>
          <textarea className="gtsr-booltext" rows={4} value={draft} autoFocus aria-label="Boolean"
            onChange={e => { setDraft(e.target.value); setBoolErr(''); }} />
          {(boolErr || booleanProblem(draft)) && <span className="gtsr-boolerr">{boolErr || booleanProblem(draft)}</span>}
          <span className="gtsr-boolacts">
            {plan.boolean_edited && (
              <button type="button" className="gtsr-link gtsr-link--muted" disabled={saving}
                onClick={async () => { setSaving(true); try { await onSaveBoolean(''); setDraft(null); } catch (e) { setBoolErr(e.message); } setSaving(false); }}>
                Go back to the plan's own
              </button>
            )}
            <button type="button" className="gtc-btn-ghost" disabled={saving} onClick={() => { setDraft(null); setBoolErr(''); }}>Cancel</button>
            <button type="button" className="gtc-primary" disabled={saving || !!booleanProblem(draft) || !draft.trim()}
              onClick={async () => { setSaving(true); try { await onSaveBoolean(draft); setDraft(null); } catch (e) { setBoolErr(e.message); } setSaving(false); }}>
              {saving ? 'Saving…' : 'Save Boolean'}
            </button>
          </span>
        </div>
      ) : plan.boolean ? (
        <div className="gtsr-boolean">
          <span className="gtsr-k">Boolean <em>{plan.boolean_edited ? 'yours · used to screen everyone found · NOT rules people out' : 'used to screen everyone found · NOT sends people to the back · paste into LinkedIn Recruiter'}</em></span>
          <code>{plan.boolean}</code>
          <span className="gtsr-boolbtns">
            {onSaveBoolean && <button type="button" className="gtsr-link" onClick={() => setDraft(plan.boolean)}>Edit</button>}
            <button type="button" className="gtsr-link" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function PlanModal({ brief, plan, base, authFetch, flash, onClose, onSaved }) {
  const [f, setF] = useState({
    titles: (plan.titles || []).join('\n'), exclude: (plan.exclude_titles || []).join('\n'),
    skills: (plan.skills || []).join('\n'), queries: (plan.queries || []).join('\n'), boolean: plan.boolean || '',
    kinds: (brief.must_haves || []).map((m, i) => { const x = (plan.must || []).find(y => y.i === i + 1); return x ? x.kind : 'searchable'; }),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  function set(k, v) { setF(p => ({ ...p, [k]: v })); }
  async function save() {
    setBusy(true); setError('');
    try {
      const must = (brief.must_haves || []).map((m, i) => {
        const x = (plan.must || []).find(y => y.i === i + 1) || {};
        return { i: i + 1, kind: f.kinds[i], signals: x.signals || [] };
      });
      const r = await authFetch(base + '/plan', { method: 'PUT', body: JSON.stringify({
        titles: lines(f.titles), exclude_titles: lines(f.exclude), skills: lines(f.skills), queries: lines(f.queries), boolean: f.boolean, must,
      }) });
      flash('Search plan saved');
      onSaved(r.plan);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsr-briefmodal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Search plan</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          <p className="gtbn-help">One per line. Your edits are kept until the brief changes. Describe the work, never the person.</p>
          <div className="gtsr-two">
            <label className="gtc-field"><span>Job titles to search</span><textarea rows={8} value={f.titles} onChange={e => set('titles', e.target.value)} /></label>
            <label className="gtc-field"><span>Skills and signals</span><textarea rows={8} value={f.skills} onChange={e => set('skills', e.target.value)} /></label>
          </div>
          <label className="gtc-field"><span>Not these titles <em className="gtbn-opt">optional</em></span><textarea rows={2} value={f.exclude} onChange={e => set('exclude', e.target.value)} /></label>
          <label className="gtc-field"><span>Web searches <em className="gtbn-opt">each describes a matching profile from a different angle</em></span><textarea rows={4} value={f.queries} onChange={e => set('queries', e.target.value)} /></label>
          <label className="gtc-field"><span>Boolean <em className="gtbn-opt">used to screen everyone found. Leave it as it is and it follows the titles and skills; change it and yours is kept.</em></span>
            <textarea rows={3} className="gtsr-mono" value={f.boolean} onChange={e => set('boolean', e.target.value)} />
            {booleanProblem(f.boolean) && <em className="gtsr-boolerr">{booleanProblem(f.boolean)}</em>}
          </label>
          {(brief.must_haves || []).length > 0 && (
            <div className="gtc-field"><span>Must-haves: searchable or a trait</span>
              <div className="gtsr-kinds">
                {brief.must_haves.map((m, i) => (
                  <div key={i} className="gtsr-kindrow">
                    <span>{m}</span>
                    <div className="gtsr-seg" role="group" aria-label={'How to treat: ' + m}>
                      {['searchable', 'trait'].map(k => (
                        <button key={k} type="button" className={'gtsr-segbtn' + (f.kinds[i] === k ? ' gtsr-segbtn--on' : '')}
                          onClick={() => set('kinds', f.kinds.map((x, j) => (j === i ? k : x)))}>{k === 'searchable' ? 'Searchable' : 'Trait'}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="gtbn-help">Traits are qualities a profile rarely shows. They are not searched for and never count against anyone.</p>
            </div>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save plan'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Starting a search ──────────────────────────────────────────────────────
function RunModal({ scope, base, authFetch, onClose, onStarted }) {
  const [est, setEst] = useState(null);
  const [rescore, setRescore] = useState(false);
  const [depth, setDepth] = useState(25);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    authFetch(base + '/estimate', { method: 'POST', body: JSON.stringify({ scope }) })
      .then(r => { setEst(r); if (r.default_depth) setDepth(r.default_depth); }).catch(e => setError(e.message));
  }, [authFetch, base, scope]);
  async function go() {
    setBusy(true); setError('');
    try {
      const r = await authFetch(base + '/run', { method: 'POST', body: JSON.stringify({ scope, rescore, depth }) });
      onStarted(r.run);
      onClose();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  const benches = scope === 'benches';
  const web = scope === 'web';
  const title = web ? 'Search the web' : benches ? 'Search your benches' : 'Search your pool';
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">{title}</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          {web ? (
            <ul className="gten-steps">
              <li><b>Searches the open web from several angles</b><span>Each search in the plan runs at once, mostly LinkedIn profiles, up to about 120. Each page is checked against the plan's titles and skills, like a Boolean, and the best are read. People already in your pool are recognised by their LinkedIn link.</span></li>
              <li><b>Judges the work, not the title</b><span>Each page is read once for professional facts only, then judged the same way as your pool. Names and locations are taken out before the fit is judged.</span></li>
              <li><b>Not saved until you accept</b><span>New people wait in the tray as suggestions. Accepting adds them as a candidate and fills their profile from their LinkedIn page. Dismissed ones are cut to a name and link; ones nobody decides on are deleted after 30 days. Nobody is contacted.</span></li>
            </ul>
          ) : (
            <ul className="gten-steps">
              <li><b>Searches {benches ? 'everyone on your active benches' : 'everyone in your pool'}</b><span>Profiles, skills, experience and CVs are checked against the plan's job titles, skills and signals, best first. Location is matched by us. Nothing outside your own records is searched.</span></li>
              <li><b>Judges fit with evidence</b><span>Each shortlisted person gets a fit score. Every must-have marked as met points to a fact in their profile. Names, contact details and location are never sent to the AI.</span></li>
              <li><b>Suggests, never adds</b><span>Results wait in the tray for you to accept or dismiss. Nobody is contacted.</span></li>
            </ul>
          )}
          {!est ? (!error && <div className="gtr-empty-sm">Working out the cost…</div>) : (
            <>
              {est.empty && <div className="gtnt-banner gtnt-banner--amber">The brief has no must-haves or nice-to-haves yet. Write the brief first.</div>}
              {est.protected && <div className="gtnt-banner gtnt-banner--amber">The brief mentions a protected characteristic. Edit it before searching.</div>}
              {est.no_exa && <div className="gtnt-banner gtnt-banner--amber">The server has no Exa key, so the web cannot be searched.</div>}
              <div className="gtc-field"><span>How many to judge, best first</span>
                <div className="gtsr-depths">
                  {(est.depths || []).map(d => (
                    <button key={d.n} type="button" className={'gtsr-depth' + (depth === d.n ? ' gtsr-depth--on' : '')} onClick={() => setDepth(d.n)}>
                      <b>{web ? d.n : d.judge}</b><span>{web ? 'people' : (d.judge < d.n ? 'everyone who matches' : 'people')}</span><em>up to {money(d.max_gbp)}</em>
                    </button>
                  ))}
                </div>
                <p className="gtbn-help">{web ? 'A web search usually takes one to three minutes.' : 'A pool search usually takes under a minute.'} You can leave the page; results land in the tray.{!web && est.pool != null ? ' ' + est.pool + (benches ? ' people on benches.' : ' people in your pool.') : ''}</p>
              </div>
              {web && est.queries && est.queries.length > 0 && (
                <div className="gtsr-query"><span>Web searches from the plan</span><ul>{est.queries.map((q, i) => <li key={i}>{q}</li>)}</ul></div>
              )}
              {!est.plan_ready && <p className="gtbn-help">The search plan will be written first, from the brief.</p>}
              <label className="gten-deep">
                <input type="checkbox" className="gtpb-check" checked={rescore} onChange={e => setRescore(e.target.checked)} />
                <span><b>Look again at people scored recently</b> Normally anyone scored for this brief in the last 30 days is skipped to save cost. People you have accepted or dismissed are never brought back.</span>
              </label>
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy || !est || !est.ready} onClick={go}>{busy ? 'Starting…' : title}</button>
        </div>
      </div>
    </div>
  );
}

// ── Deciding ───────────────────────────────────────────────────────────────
function AcceptModal({ kind, ownerId, items, buckets, base, authFetch, flash, onClose, onDone }) {
  const [stages, setStages] = useState(kind === 'bench' ? null : []);
  const [stageId, setStageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [noticeIds, setNoticeIds] = useState(null);
  const [bulkNoticeIds, setBulkNoticeIds] = useState(null);
  useEffect(() => {
    if (kind !== 'role') return;
    authFetch('/api/roles/' + ownerId + '/stages').then(r => {
      const st = (r.stages || []).filter(s => s.outcome === 'open' && s.side !== 'client');
      setStages(st);
      const entry = st.find(s => s.key === 'sourced') || st.find(s => s.is_entry) || st[0];
      setStageId(entry ? entry.id : '');
    }).catch(() => setStages([]));
  }, [authFetch, kind, ownerId]);
  const [done, setDone] = useState(0);
  async function go() {
    setBusy(true); setError(''); setDone(0);
    try {
      // In small groups, so the count moves and a long list never times out.
      const r = { added: 0, already_there: 0, failed: 0, results: [], notices: [] };
      const all = items.map(x => x.id);
      for (let i = 0; i < all.length; i += ACCEPT_CHUNK) {
        const part = await authFetch(base + '/decide', { method: 'POST', body: JSON.stringify({ ids: all.slice(i, i + ACCEPT_CHUNK), action: 'accept', stage_id: stageId === '' ? null : Number(stageId) }) });
        r.added += part.added || 0; r.already_there += part.already_there || 0; r.failed += part.failed || 0;
        r.results = r.results.concat(part.results || []); r.notices = r.notices.concat(part.notices || []);
        setDone(Math.min(all.length, i + ACCEPT_CHUNK));
      }
      setResult(r);
      onDone();
      const ids = (r.notices || []).map(n => n.id);
      if (ids.length === 1) setNoticeIds(ids); else if (ids.length > 1) setBulkNoticeIds(ids);
      if (!r.failed && !ids.length) { flash(plural(r.added, 'person', 'people') + ' added' + (kind === 'role' ? ' to the pipeline.' : ' to the bench.')); onClose(); }
    } catch (e) {
      // Groups already added stay added: refresh so the tray shows it.
      setError(e.message); onDone();
    }
    setBusy(false);
  }
  const opts = kind === 'role'
    ? (stages || []).map(s => ({ value: s.id, label: s.label }))
    : [{ value: '', label: 'Not in a bucket' }].concat((buckets || []).map(b => ({ value: b.id, label: b.label })));
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Accept {items.length === 1 ? items[0].full_name : plural(items.length, 'person', 'people')}</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          {result ? (
            <div className={'gtnt-banner ' + (result.failed ? 'gtnt-banner--amber' : 'gtnt-banner--green')}>
              <b>{result.added} added.</b>
              {result.already_there > 0 && ' ' + result.already_there + ' were already there.'}
              {result.failed > 0 && ' ' + result.failed + ' could not be added.'}
              <ul className="gtnt-blocked">
                {result.results.filter(x => x.result !== 'added').map(x => <li key={x.id}><b>{x.name || 'Someone'}</b> {x.result === 'already_there' ? 'was already there' : x.result === 'already_decided' ? 'was already decided' : x.message}</li>)}
              </ul>
            </div>
          ) : (
            <>
              <p className="gtbn-help">{kind === 'role'
                ? 'They join this role\'s pipeline at the stage you pick. Nobody is emailed.'
                : 'They go onto this bench. A bench never contacts anyone.'}</p>
              {items.some(x => x.is_prospect) && (
                <p className="gtbn-help">{items.filter(x => x.is_prospect).length === 1 ? 'One is' : items.filter(x => x.is_prospect).length + ' are'} from the web and not in your pool yet. Accepting adds them as a candidate, and their profile is filled from their LinkedIn page.</p>
              )}
              <div className="gtc-field"><span>{kind === 'role' ? 'Stage' : 'Bucket'}</span>
                {kind === 'role' && stages === null ? <div className="gtr-empty-sm">Loading…</div>
                  : <GtSelect value={stageId} onChange={setStageId} options={opts} placeholder={kind === 'role' ? 'Choose a stage' : 'Not in a bucket'} />}
              </div>
            </>
          )}
        </div>
        <div className="gtc-modal-foot">
          {result ? <button className="gtc-primary" onClick={onClose}>Done</button> : (
            <>
              <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button className="gtc-primary" disabled={busy || (kind === 'role' && !stageId)} onClick={go}>{busy ? (items.length > ACCEPT_CHUNK ? 'Adding ' + done + ' of ' + items.length + '…' : 'Adding…') : 'Accept ' + items.length}</button>
            </>
          )}
        </div>
        {noticeIds && <GtNoticeModal ids={noticeIds} authFetch={authFetch} onClose={() => { setNoticeIds(null); onClose(); }} />}
        {bulkNoticeIds && <GtBulkNoticeModal ids={bulkNoticeIds} authFetch={authFetch} onClose={() => { setBulkNoticeIds(null); onClose(); }} />}
      </div>
    </div>
  );
}

function DismissModal({ items, base, authFetch, flash, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function go() {
    setBusy(true); setError('');
    try {
      const r = await authFetch(base + '/decide', { method: 'POST', body: JSON.stringify({ ids: items.map(x => x.id), action: 'dismiss', reason, note }) });
      flash(plural(r.dismissed, 'suggestion', 'suggestions') + ' dismissed.');
      onDone(); onClose();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">Dismiss {items.length === 1 ? items[0].full_name : plural(items.length, 'suggestion', 'suggestions')}</div>
        <div className="gtc-modal-body">
          {error && <div className="gtnt-banner gtnt-banner--amber">{error}</div>}
          <p className="gtbn-help">The reason is kept, so future searches learn what does not fit. They will not be suggested for this brief again.</p>
          <div className="gtc-field"><span>Reason</span><GtSelect value={reason} onChange={setReason} options={DISMISS} placeholder="Choose a reason" /></div>
          <label className="gtc-field"><span>Note <em className="gtbn-opt">optional</em></span>
            <input value={note} maxLength={500} onChange={e => setNote(e.target.value)} placeholder="Anything worth remembering" />
          </label>
        </div>
        <div className="gtc-modal-foot">
          <button className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="gtc-primary" disabled={busy || !reason} onClick={go}>{busy ? 'Saving…' : 'Dismiss'}</button>
        </div>
      </div>
    </div>
  );
}

// ── One suggestion ─────────────────────────────────────────────────────────
function Evidence({ src }) {
  if (!src || !src.length) return null;
  return (
    <ul className="gtsr-ev">
      {src.map((x, i) => (
        <li key={i}><em>{x.o}</em><span>{x.t}</span>{x.u && <a href={x.u} target="_blank" rel="noreferrer">Source</a>}</li>
      ))}
    </ul>
  );
}

// Where they are, against where the brief asks for. Matched by us, not the AI.
function PlaceTag({ fit, place: full }) {
  const bits = String(full || '').split(',').map(x => x.trim()).filter(Boolean);
  const place = bits.length > 2 ? bits[0] + ', ' + bits[bits.length - 1] : bits.join(', ');
  if (fit === 'outside') return <span className="gtsr-tag gtsr-tag--away" title="In another country from the role, so a weaker match">Outside the area · {place}</span>;
  if (fit === 'country') return <span className="gtsr-tag gtsr-tag--near" title="Same country, a different city">{place} · other city</span>;
  if (fit === 'unknown') return <span className="gtsr-tag gtsr-tag--faint" title="Their profile does not show where they are">Location not shown</span>;
  if (!place) return null;
  return <span className={'gtsr-tag' + (fit === 'in' ? ' gtsr-tag--here' : '')}>{place}</span>;
}

function SuggestionCard({ s, kind, view, checked, onCheck, onAccept, onDismiss, onOpen }) {
  const [open, setOpen] = useState(false);
  const must = s.must_haves || [];
  const hard = must.filter(m => m.kind !== 'trait');
  const metN = hard.filter(m => m.met === 'yes').length;
  const role = [s.current_title, s.current_company].filter(Boolean).join(' at ');
  return (
    <article className={'gtsr-card gtsr-card--' + s.band + (checked ? ' gtsr-card--sel' : '')}>
      <div className="gtsr-card-top">
        {view === 'new' && <input type="checkbox" className="gtpb-check" checked={checked} onChange={onCheck} aria-label={'Select ' + s.full_name} />}
        <div className={'gtsr-score gtsr-score--' + s.band} title={BAND_LABEL[s.band]}><b>{s.score}</b><span>fit</span></div>
        <span className="gtsr-av" style={{ background: avatarColour(s.full_name) }}>{initials(s.full_name)}</span>
        <div className="gtsr-who">
          {s.is_prospect
            ? <a className="gtsr-name" href={s.profile_url} target="_blank" rel="noreferrer" title="Open their public profile">{s.full_name} <span className="gtsr-ext" aria-hidden="true">↗</span></a>
            : <button type="button" className="gtsr-name" onClick={onOpen}>{s.full_name}</button>}
          <span className="gtsr-line">{role || s.headline || 'No current role on file'}</span>
          <span className="gtsr-tags">
            <span className={'gtsr-band gtsr-band--' + s.band}>{BAND_LABEL[s.band]}</span>
            {s.is_prospect && <span className="gtsr-tag gtsr-tag--web">From the web · not in your pool</span>}
            {!s.is_prospect && s.source === 'web' && <span className="gtsr-tag gtsr-tag--web">Found on the web</span>}
            {kind === 'role' && s.source === 'bench' && <span className="gtsr-tag gtsr-tag--bench">From your benches</span>}
            {(s.benches || []).slice(0, 2).map(b => <span key={b.id} className="gtsr-tag">On {b.name}</span>)}
            {s.expertise_domain && <span className="gtsr-tag">{words(s.expertise_domain)}</span>}
            <PlaceTag fit={s.location_fit} place={s.location} />
          </span>
        </div>
        <div className="gtsr-acts">
          {view === 'new' ? (
            <>
              <button type="button" className="gtc-btn-ghost" onClick={onDismiss}>Dismiss</button>
              <button type="button" className="gtc-primary" onClick={onAccept}>Accept</button>
            </>
          ) : view === 'accepted' ? (
            <span className="gtsr-decided">Accepted {niceDate(s.decided_at)}{s.decided_by_name ? ' by ' + s.decided_by_name : ''}</span>
          ) : (
            <span className="gtsr-decided">Dismissed: {DISMISS_LABEL[s.dismiss_reason] || 'no reason'}{s.dismiss_note ? ' · ' + s.dismiss_note : ''}</span>
          )}
        </div>
      </div>

      {must.length > 0 && (
        <div className="gtsr-must">
          <span className="gtsr-k">Must-haves {metN} of {hard.length} shown{must.length > hard.length ? ' · ' + (must.length - hard.length) + ' traits' : ''}</span>
          <ul>
            {must.filter(m => m.kind !== 'trait' || m.met !== 'unknown').map((m, i) => (
              <li key={i} className={'gtsr-m gtsr-m--' + m.met + (m.kind === 'trait' ? ' gtsr-m--trait' : '')}>
                <span className="gtsr-m-ic" aria-hidden="true">{m.met === 'yes' ? '✓' : m.met === 'no' ? '✕' : m.kind === 'trait' ? '·' : '?'}</span>
                <span className="gtsr-m-req">{m.req}</span>
                <span className="gtsr-m-note">{m.met === 'yes' ? (m.quote ? '“' + m.quote + '”' : 'Shown') : m.met === 'no' ? (m.quote ? 'Against: “' + m.quote + '”' : 'Profile says otherwise') : m.kind === 'trait' ? 'A trait: for the interview, not the profile' : 'Their profile does not say'}</span>
              </li>
            ))}
          </ul>
          {must.some(m => m.kind === 'trait' && m.met === 'unknown') && (
            <p className="gtsr-traits"><b>For the interview</b> {must.filter(m => m.kind === 'trait' && m.met === 'unknown').map(m => m.req).join(' · ')}</p>
          )}
        </div>
      )}

      {(s.reasons || []).length > 0 && (
        <ul className="gtsr-reasons">{s.reasons.map((r, i) => <li key={i}>{r.text}</li>)}</ul>
      )}
      {(s.gaps || []).length > 0 && <p className="gtsr-gaps"><b>Gaps</b> {s.gaps.join(' · ')}</p>}

      <button type="button" className="gtsr-evbtn" onClick={() => setOpen(o => !o)}>{open ? 'Hide evidence' : 'Show the evidence'}</button>
      {open && (
        <div className="gtsr-evwrap">
          {must.filter(m => m.met === 'yes').map((m, i) => <div key={'m' + i} className="gtsr-evgroup"><span className="gtsr-k">{m.req}</span><Evidence src={m.src} /></div>)}
          {(s.reasons || []).map((r, i) => <div key={'r' + i} className="gtsr-evgroup"><span className="gtsr-k">{r.text}</span><Evidence src={r.src} /></div>)}
          <p className="gtsr-meta">{s.is_prospect
            ? 'Judged ' + niceDate(s.updated_at) + ' by artificial intelligence from facts on their public profile, with their name and location taken out.'
            : 'Judged ' + niceDate(s.updated_at) + ' by artificial intelligence from facts in their profile, without their name, contact details or location.'}</p>
        </div>
      )}
    </article>
  );
}

// ── Progress and the funnel ────────────────────────────────────────────────
const STAGE_LABEL = { planning: 'Writing the search plan', searching: 'Searching', screening: 'Screening against the plan', reading: 'Reading profiles', judging: 'Judging fit', done: 'Finishing' };
function elapsed(from, now) {
  const s = Math.max(0, Math.round((now - new Date(from).getTime()) / 1000));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
function RunProgress({ run, now }) {
  const web = run.scope === 'web';
  const stages = web ? ['planning', 'searching', 'screening', 'reading', 'judging'] : ['planning', 'searching', 'judging'];
  const at = Math.max(0, stages.indexOf(run.stage || 'planning'));
  // Each stage is a share of the bar; reading and judging fill as they go.
  const share = 100 / stages.length;
  let within = 0.3;
  if (run.stage === 'reading' && run.shortlisted) within = run.read_count / run.shortlisted;
  if (run.stage === 'judging' && run.shortlisted) within = run.scored / run.shortlisted;
  const pct = Math.min(99, Math.round(at * share + within * share));
  let detail = '';
  if (run.stage === 'planning') detail = 'Working out the titles, skills and searches from the brief';
  else if (run.stage === 'searching') detail = web ? 'Running the web searches from the plan' : 'Matching your records against the plan';
  else if (run.stage === 'screening') detail = 'Found ' + run.found + ' profiles, ' + run.relevant + ' match the plan';
  else if (run.stage === 'reading') detail = 'Found ' + run.found + ', ' + run.relevant + ' match the plan. Read ' + run.read_count + ' of ' + run.shortlisted;
  else if (run.stage === 'judging') detail = (web ? run.found + ' found, ' + run.relevant + ' matched the plan. ' : '') + 'Judged ' + run.scored + ' of ' + run.shortlisted;
  return (
    <div className="gten-progress gtsr-progress" role="status">
      <div className="gten-progress-text">
        <b>{(web ? 'Web search' : run.scope === 'benches' ? 'Bench search' : 'Pool search') + ': ' + (STAGE_LABEL[run.stage] || 'Starting')}</b>
        <span>{detail}{run.created_at ? ' · ' + elapsed(run.created_at, now) : ''}</span>
      </div>
      <div className="gten-progress-bar"><span style={{ width: Math.max(4, pct) + '%' }} /></div>
      <ol className="gtsr-stages">
        {stages.map((st, i) => <li key={st} className={i < at ? 'gtsr-st--done' : i === at ? 'gtsr-st--on' : ''}>{STAGE_LABEL[st]}</li>)}
      </ol>
      <p className="gtsr-progress-note">{web ? 'A web search usually takes one to three minutes.' : 'Usually under a minute.'} You can leave this page; results land in the tray.</p>
    </div>
  );
}
function Funnel({ run }) {
  const web = run.scope === 'web';
  const when = niceDate(run.finished_at || run.created_at);
  if (run.status === 'failed') {
    return <div className="gtsr-funnel gtsr-funnel--bad"><b>Last search did not finish ({when})</b><span>{run.error || 'Run it again.'}</span></div>;
  }
  if (!run.stage) {
    // A search from before the funnel was recorded: only the judged count is known.
    return (
      <div className="gtsr-funnel">
        <b>Last {web ? 'web' : run.scope === 'benches' ? 'bench' : 'pool'} search, {when}</b>
        <span className="gtsr-funnel-steps"><span><b>{run.scored || 0}</b> judged</span></span>
        <span className="gtsr-funnel-bands">
          <span className="gtsr-funnel-cost">older search, before the search plan</span>
          {run.cost_gbp != null && <span className="gtsr-funnel-cost">cost {money(run.cost_gbp)}</span>}
        </span>
      </div>
    );
  }
  const steps = web
    ? [['found', run.found], ['matched the plan', run.relevant], ['read', run.read_count], ['judged', run.scored]]
    : [['searched', run.found || run.considered], ['matched the plan', run.relevant || run.shortlisted], ['judged', run.scored]];
  return (
    <div className="gtsr-funnel">
      <b>Last {web ? 'web' : run.scope === 'benches' ? 'bench' : 'pool'} search, {when}</b>
      <span className="gtsr-funnel-steps">{steps.map(([l, n], i) => <span key={l}>{i > 0 && <em aria-hidden="true">›</em>}<b>{n || 0}</b> {l}</span>)}</span>
      <span className="gtsr-funnel-bands">
        <i className="gtsr-dot gtsr-dot--green" /><b>{run.strong_n || 0}</b> strong
        <i className="gtsr-dot gtsr-dot--amber" /><b>{run.possible_n || 0}</b> possible
        <i className="gtsr-dot gtsr-dot--slate" /><b>{run.weak_n || 0}</b> weak
        {run.cost_gbp != null && <span className="gtsr-funnel-cost">cost {money(run.cost_gbp)}</span>}
      </span>
    </div>
  );
}

// ── The panel ──────────────────────────────────────────────────────────────
export default function GtSourcingPanel({ kind, ownerId, getToken, buckets, flash: flashProp, onOpenCandidate, onChanged }) {
  const authFetch = useSourcingFetch(getToken);
  const base = '/api/sourcing/' + kind + '/' + ownerId;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('new');
  const [showWeak, setShowWeak] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [editBrief, setEditBrief] = useState(false);
  const [runScope, setRunScope] = useState(null);
  const [run, setRun] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [editPlan, setEditPlan] = useState(false);
  const [building, setBuilding] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [toast, setToast] = useState(null);
  const timer = useRef(null);

  const flash = useCallback((msg, isErr) => {
    if (flashProp) { flashProp(msg, isErr); return; }
    setToast({ msg, err: !!isErr }); setTimeout(() => setToast(null), isErr ? 7000 : 3500);
  }, [flashProp]);

  const loadInfo = useCallback(async () => {
    try { const r = await authFetch(base); setInfo(r); if (r.summary && r.summary.running) setRun(cur => cur || r.summary.running); return r; }
    catch (e) { setError(e.message); return null; }
  }, [authFetch, base]);
  const loadItems = useCallback(async () => {
    try {
      const r = await authFetch(base + '/suggestions?status=' + view + '&min=' + (view === 'new' && !showWeak ? 50 : 0));
      setItems(r.suggestions || []);
      setSelected(sel => sel.filter(id => (r.suggestions || []).some(x => x.id === id)));
    } catch (e) { setError(e.message); }
  }, [authFetch, base, view, showWeak]);

  useEffect(() => { loadInfo(); }, [loadInfo]);
  useEffect(() => { setItems(null); loadItems(); }, [loadItems]);
  useEffect(() => { setSelected([]); }, [view]);

  // Follow a running search to the end.
  const runId = run && run.status === 'running' ? run.id : null;
  useEffect(() => {
    if (!runId) return undefined;
    let live = true;
    const tick = async () => {
      try {
        const r = await authFetch('/api/sourcing/runs/' + runId);
        if (!live) return;
        setRun(r.run);
        if (r.run.status === 'running') { timer.current = setTimeout(tick, 1500); return; }
        await Promise.all([loadInfo(), loadItems()]);
        if (onChanged) onChanged();
        if (r.run.status === 'failed') flash(r.run.error || 'The search did not finish.', true);
        else flash(r.run.scored ? plural(r.run.scored, 'person', 'people') + ' judged: ' + r.run.strong_n + ' strong, ' + r.run.possible_n + ' possible, ' + r.run.weak_n + ' weak.'
          : r.run.shortlisted ? 'Nobody could be judged. Try again in a moment.'
          : r.run.scope === 'web' ? (r.run.considered ? 'The profiles found were already suggested, decided, or not people.' : 'The web search found nobody for this brief. Try wording the must-haves more plainly.')
          : 'Nobody new in your ' + (r.run.scope === 'benches' ? 'benches' : 'pool') + ' matches this brief.');
      } catch (e) { if (live) timer.current = setTimeout(tick, 4000); }
    };
    tick();
    return () => { live = false; if (timer.current) clearTimeout(timer.current); };
  }, [runId, authFetch, loadInfo, loadItems, onChanged, flash]);

  const sel = useMemo(() => (items || []).filter(x => selected.indexOf(x.id) !== -1), [items, selected]);
  // A clock for the time-so-far while a search runs.
  useEffect(() => {
    if (!runId) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runId]);
  async function saveBoolean(text) {
    await authFetch(base + '/plan', { method: 'PUT', body: JSON.stringify({ boolean: text }) });
    await loadInfo();
    flash(text ? 'Boolean saved. Searches use it from now on.' : 'Boolean follows the plan again');
  }
  async function buildPlan() {
    setBuilding(true);
    try { await authFetch(base + '/plan/rebuild', { method: 'POST' }); await loadInfo(); flash('Search plan written'); }
    catch (e) { flash(e.message, true); }
    setBuilding(false);
  }

  if (error && !info) return <div className="gtc-error">{error}</div>;
  if (!info) return <div className="gtr-empty-sm">Loading…</div>;
  const sum = info.summary || {};
  const running = run && run.status === 'running';
  const last = sum.last_run;
  const afterDecide = () => { loadItems(); loadInfo(); if (onChanged) onChanged(); };

  return (
    <div className="gtsr">
      <BriefCard brief={info.brief} saved={info.saved} kind={kind} plan={info.plan} building={building}
        onEdit={() => setEditBrief(true)} onEditPlan={() => setEditPlan(true)} onBuildPlan={buildPlan} onSaveBoolean={saveBoolean} />

      <div className="gtsr-bar">
        <div className="gtsr-views" role="tablist">
          {VIEWS.map(v => (
            <button key={v.key} type="button" role="tab" aria-selected={view === v.key} className={'gtsr-view' + (view === v.key ? ' gtsr-view--on' : '')} onClick={() => setView(v.key)}>
              {v.label}{v.key === 'new' && sum.new ? <span className="gtsr-n">{sum.new}</span> : null}
            </button>
          ))}
        </div>
        <div className="gtsr-spacer" />

        {kind === 'role' && <button type="button" className="gtc-btn-ghost" disabled={running} onClick={() => setRunScope('benches')}>Search your benches</button>}
        <button type="button" className="gtc-btn-ghost" disabled={running} onClick={() => setRunScope('pool')}>Search your pool</button>
        <button type="button" className="gtc-primary" disabled={running} onClick={() => setRunScope('web')}>Search the web</button>
      </div>

      {running && <RunProgress run={run} now={now} />}
      {!running && last && <Funnel run={last} />}

      {kind === 'role' && view === 'new' && sum.bench_fits > 0 && (
        <div className="gtsr-benchfit"><b>{plural(sum.bench_fits, 'person', 'people')} on your benches {sum.bench_fits === 1 ? 'fits' : 'fit'} this role.</b> They are marked "From your benches" below.</div>
      )}

      {view === 'new' && selected.length > 0 && (
        <div className="gtbn-bulk">
          <b>{selected.length} selected</b>
          <button className="gtc-primary" onClick={() => setAccepting(sel)}>Accept {selected.length}</button>
          <button className="gtc-btn-ghost" onClick={() => setDismissing(sel)}>Dismiss {selected.length}</button>
          <div className="gtbn-spacer" />
          <button className="gtc-btn-ghost" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      {items === null ? <div className="gtr-empty-sm">Loading…</div> : items.length === 0 ? (
        <div className="gtsr-empty">
          <div className="gtsr-empty-mark">✦</div>
          {view === 'new' ? (
            <>
              <h3>{last ? 'Nothing waiting' : 'No suggestions yet'}</h3>
              <p>{last ? 'Everyone suggested so far has been decided, or nobody fitted well enough. Search again after adding people or changing the brief.'
                : 'Write the brief, then search your pool first, then the web. You see who fits and the evidence for it, and nobody is added until you accept them.'}</p>
              {!showWeak && sum.weak > 0 && <button type="button" className="gtc-btn-ghost" onClick={() => setShowWeak(true)}>Show {plural(sum.weak, 'weaker match', 'weaker matches')}</button>}
            </>
          ) : <p>{view === 'accepted' ? 'Nobody accepted from suggestions yet.' : 'Nothing dismissed yet.'}</p>}
        </div>
      ) : (
        <>
          {view === 'new' && (
            <div className="gtsr-listhead">
              <label className="gtsr-all">
                <input type="checkbox" className="gtpb-check" checked={items.length > 0 && items.every(x => selected.indexOf(x.id) !== -1)}
                  onChange={e => setSelected(e.target.checked ? items.map(x => x.id) : [])} aria-label="Select all suggestions" />
                <span>{plural(items.length, 'person', 'people')}, best fit first</span>
              </label>
              {(showWeak || sum.weak > 0) && <button type="button" className="gtsr-link" onClick={() => setShowWeak(w => !w)}>{showWeak ? 'Hide weaker matches' : 'Show ' + plural(sum.weak, 'weaker match', 'weaker matches')}</button>}
            </div>
          )}
          <div className="gtsr-list">
            {items.map(s => (
              <SuggestionCard key={s.id} s={s} kind={kind} view={view}
                checked={selected.indexOf(s.id) !== -1}
                onCheck={() => setSelected(cur => (cur.indexOf(s.id) === -1 ? cur.concat([s.id]) : cur.filter(x => x !== s.id)))}
                onAccept={() => setAccepting([s])} onDismiss={() => setDismissing([s])}
                onOpen={() => onOpenCandidate && onOpenCandidate(s)} />
            ))}
          </div>
        </>
      )}

      {editPlan && info.plan && <PlanModal brief={info.brief} plan={info.plan} base={base} authFetch={authFetch} flash={flash}
        onClose={() => setEditPlan(false)} onSaved={() => { setEditPlan(false); loadInfo(); }} />}
      {editBrief && <BriefModal kind={kind} brief={info.brief} base={base} authFetch={authFetch} flash={flash}
        onClose={() => setEditBrief(false)} onSaved={() => { setEditBrief(false); loadInfo(); }} />}
      {runScope && <RunModal scope={runScope} base={base} authFetch={authFetch} onClose={() => setRunScope(null)}
        onStarted={r => setRun({ ...r, stage: 'planning', created_at: new Date().toISOString(), scored: 0, shortlisted: 0, found: 0, relevant: 0, read_count: 0 })} />}
      {accepting && <AcceptModal kind={kind} ownerId={ownerId} items={accepting} buckets={buckets} base={base} authFetch={authFetch} flash={flash}
        onClose={() => setAccepting(null)} onDone={() => { setSelected([]); afterDecide(); }} />}
      {dismissing && <DismissModal items={dismissing} base={base} authFetch={authFetch} flash={flash}
        onClose={() => setDismissing(null)} onDone={() => { setSelected([]); afterDecide(); }} />}
      {toast && <div className={'gtc-toast' + (toast.err ? ' gtc-toast--err' : '')} role="status">{toast.msg}</div>}
    </div>
  );
}
